
import { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUser, userLoggedOut as userLoggedOutAuth } from "store/auth/userSlice";
import { apiSignIn, apiSignUp, apigetUserDetails } from "services/AuthService";
import { toast, Notification } from "components/ui";
import * as Toast from 'react-toastify'
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import { useNavigate } from "react-router-dom";
import useQuery from "./useQuery";
import { setProfile, userLoggedOut as userLoggedOutProfile } from "store/profile/userSlice";

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const { token, signedIn } = useSelector((state) => state.auth.session);

  const [authLoading, setAuthLoading] = useState(true);

  const handleSignOut = useCallback(() => {
    localStorage.removeItem("userDetails");
    localStorage.removeItem("access_token");
    localStorage.removeItem("login_time");
    dispatch(onSignOutSuccess());
    dispatch(userLoggedOutAuth());
    dispatch(userLoggedOutProfile());

    // Use window.location.href for a clean slate, ensuring no stale memory state exists
    // window.location.href = '/sign-in'; //this causes page reload
    navigate('/sign-in')
  }, [dispatch]);

  const signOut = useCallback(() => {
    handleSignOut();
  }, [handleSignOut]);

  const signIn = async (values) => {
    try {
      const resp = await apiSignIn(values);
      const { data, status } = resp;

      if (data && status === 200 && !["failed", "Failed", "Error"].includes(data.status)) {
        dispatch(onSignInSuccess(data.token));
        localStorage.setItem("access_token", data.token);
        localStorage.setItem("login_time", Date.now().toString());

        const userResp = await apigetUserDetails(data.userId);
        if (userResp.status === 200 && userResp.data) {
          const userDetails = userResp.data;
          dispatch(setProfile(userDetails))
          localStorage.setItem("userDetails", JSON.stringify(userDetails));
          Toast.toast.success(
            " You have successfully signed in.",
            {
              autoClose: 4000,
              position: "top-center",
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "colored",
              style: {
                background: '#00342b'
              }
            }
          );
          
          const redirectUrl = query.get("redirectUrl");

          const userRoles = userDetails?.userRoles || [];

          // ✅ Redirect based on role
          if (userRoles.includes("player")) {
            navigate(redirectUrl || "/");
          } else {
            navigate("/home");
          }
          // window.location.reload()
          return { status: "success" };
        }
      } else {
        Toast.toast.error(
          data?.message || data?.response || "Something went wrong. Please try again.",
          {
            autoClose: 4000,
            position: "top-center",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            style: {
              backgroundColor: "#00342b"
            }
          }
        );

        // signOut();
      }
    } catch (error) {
      signOut();
      navigate("/sign-in");
      return Toast.toast.error(
        "Something went wrong. Please try again.",
        {
          position: "top-center"
        }
      );
    }
  };

  const signUp = async (values) => {
    try {
      const resp = await apiSignUp(values);
      const { data } = resp;
      const status = data?.status?.toLowerCase();

      if (status === "success") {
        Toast.toast.success(
          data.message,
          {
            autoClose: 4000,
            position: "top-center",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            style: {
              backgroundColor: "#00342b"
            }
          }
        );

        navigate("/sign-in");
        return { status: "success", message: data?.message || "" };
      }

      Toast.toast.error(
        "Sign Up Failed",
        {
          autoClose: 4000,
          position: "top-center",
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
          style: {
            backgroundColor: "#00342b"
          }
        }
      );
      return { status: "failed", message: data?.message || "Signup failed" };
    } catch (error) {
      toast.push(
        <Notification title="Sign Up Error" type="danger" duration={4000}>
          {error?.response?.data?.message || "Something went wrong."}
        </Notification>,
        { placement: "top-center" }
      );
      return {
        status: "failed",
        message: error?.response?.data?.message || "Something went wrong.",
      };
    }
  };

  useEffect(() => {
    const checkSessionExpiration = () => {
      const loginTime = localStorage.getItem("login_time");
      if (loginTime) {
        const elapsed = Date.now() - parseInt(loginTime, 10);
        const sessionTimeout = 12 * 60 * 60 * 1000;

        if (elapsed >= sessionTimeout) {
          handleSignOut();
          toast.push(
            <Notification title="Session Expired" type="warning" duration={4500}>
              Your session has expired due to inactivity.
            </Notification>,
            { placement: "top-center" }
          );
        } else {
          setTimeout(checkSessionExpiration, sessionTimeout - elapsed);
        }
      }
      setAuthLoading(false);
    };

    checkSessionExpiration();
  }, [handleSignOut]);

  return {
    authenticated: Boolean(token && signedIn),
    authLoading,
    signIn,
    signUp,
    signOut,
  };
}

export default useAuth;
